<script setup>
import { ref } from "vue";
import { useResizeObserver } from "@vueuse/core";
import { useGlobalStore } from "@stores/global";
import { useGlobeStore } from "@stores/globe";
import ToggleInput from "@components/ToggleInput.vue";
import Button from "@components/Button.vue";
import PanelTabs from "@components/explore/PanelTabs.vue";
import PanelFooter from "@components/explore/PanelFooter.vue";

const globalStore = useGlobalStore();
const globeStore = useGlobeStore();

function openBehindTheScenes() {
    globalStore.showPanel("behindTheScenes");
}

// Publish this panel's height so the mobile behind-the-scenes overlay can pad
// its scroll area and never hide clips behind this footer.
const rootEl = ref(null);
useResizeObserver(rootEl, ([entry]) => {
    document.documentElement.style.setProperty(
        "--mobile-controls-h",
        `${entry.target.offsetHeight}px`,
    );
});
</script>

<template>
    <Transition name="fade" appear>
        <div
            v-if="!globeStore.selectedCountry"
            ref="rootEl"
            class="toggle-controls bg-marine-800 flex flex-col max-lg:gap-6 max-lg:p-6"
            :class="{
                'lg:hidden': globalStore.panelActive('behindTheScenes'),
                'max-lg:hidden': globalStore.mobilePanel !== 'extras',
            }"
        >
            <PanelTabs />

            <!-- On mobile this is the shared 180px content box (matching the
                 other tab panels); on desktop it collapses (lg:contents) so the
                 button and toggles flow directly into the panel column. -->
            <div
                class="flex flex-col gap-6 max-lg:h-[180px] max-lg:overflow-y-auto lg:contents"
            >
                <Button
                    className="explore-bts h-[38px] min-h-0! w-full gap-3! tracking-[0.12px]"
                    @click="openBehindTheScenes"
                >
                    Explore behind the scenes
                </Button>

                <div
                    class="flex flex-col gap-6 lg:items-center lg:p-6"
                >
                    <p
                        class="body-xs faded-text leading-[1.2] font-medium lg:text-center"
                    >
                        Questions? Contact us at
                        <a
                            href="mailto:ishowspeed@expediagroup.com"
                            class="text-yellow-400 hover:underline"
                        >
                            ishowspeed@expediagroup.com
                        </a>
                    </p>
                    <div class="flex flex-col gap-3 max-lg:w-full lg:items-end">
                        <ToggleInput
                            class="max-lg:w-full"
                            label="Show Routes"
                            :model-value="globeStore.showLines"
                            @update:model-value="globeStore.toggleLines()"
                        />
                        <ToggleInput
                            class="max-lg:w-full"
                            label="Auto Rotate"
                            :model-value="globeStore.autoRotateEnabled"
                            @update:model-value="globeStore.toggleAutoRotate()"
                        />
                    </div>
                </div>
            </div>

            <PanelFooter class="lg:hidden" />
        </div>
    </Transition>
</template>

<style scoped>
.toggle-controls {
    grid-column: -5 / -1;
    grid-row: -5 / -1;
    align-self: end;
    z-index: 3;
}

@media (max-width: 1024px) {
    .toggle-controls {
        grid-column: 1 / -1;
        grid-row: -2 / -1;
        align-self: end;
    }
}

/* Notched bottom-right corner to match the design (stripe texture comes
   from Button's -primary variant). */
.explore-bts {
    clip-path: polygon(
        0 0,
        100% 0,
        100% calc(100% - 8px),
        calc(100% - 8px) 100%,
        0 100%
    );
}
</style>
